import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
} from '@tanstack/react-router';
import { MarketingLayout } from './components/MarketingLayout.tsx';
import { useCanonicalUrl } from './hooks/useCanonicalUrl.ts';
import { usePageView } from './hooks/usePageView.ts';
import { Home } from './pages/Home.tsx';
import { Pricing } from './pages/Pricing.tsx';
import { Demo } from './pages/Demo.tsx';
import { FeatureDocuments } from './pages/features/Documents.tsx';
import { FeatureApprovals } from './pages/features/Approvals.tsx';
import { FeatureAI } from './pages/features/AI.tsx';
import { DocsGettingStarted } from './pages/docs/GettingStarted.tsx';
import { DocsWorkspace } from './pages/docs/Workspace.tsx';
import { DocsDocuments } from './pages/docs/Documents.tsx';
import { DocsChangeManagement } from './pages/docs/ChangeManagement.tsx';
import { DocsApprovalRules } from './pages/docs/ApprovalRules.tsx';
import { DocsDetails } from './pages/docs/Details.tsx';
import { DocsTeams } from './pages/docs/Teams.tsx';
import { DocsAiIntegration } from './pages/docs/AiIntegration.tsx';
import { BlogIndex } from './pages/blog/BlogIndex.tsx';
import { blogPosts, blogPostPath } from './pages/blog/posts.ts';
import { Privacy } from './pages/Privacy.tsx';
import { Terms } from './pages/Terms.tsx';

// Root route wraps every page in the lean marketing chrome.
const rootRoute = createRootRoute({
  component: function RootLayout() {
    useCanonicalUrl();
    // Covers every route. The only call site — a page adding its own would
    // double-count the visit.
    usePageView();
    return (
      <MarketingLayout>
        <Outlet />
      </MarketingLayout>
    );
  },
});

// Every route here is a public marketing page. `pageType: 'marketing'` is
// read by useDocumentTitle to format the <title>; it mirrors the tag the
// product app uses so the shared page modules behave identically. The path
// list is also mirrored in docker-entrypoint.sh's sitemap — update both
// together when marketing pages change.
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Home,
  staticData: { pageType: 'marketing' },
});

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: Pricing,
  staticData: { pageType: 'marketing' },
});

const demoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/demo',
  component: Demo,
  staticData: { pageType: 'marketing' },
});

const featureDocumentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/features/documents',
  component: FeatureDocuments,
  staticData: { pageType: 'marketing' },
});

const featureApprovalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/features/approvals',
  component: FeatureApprovals,
  staticData: { pageType: 'marketing' },
});

const featureAIRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/features/ai',
  component: FeatureAI,
  staticData: { pageType: 'marketing' },
});

const docsGettingStartedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs',
  component: DocsGettingStarted,
  staticData: { pageType: 'marketing' },
});

const docsWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/workspace',
  component: DocsWorkspace,
  staticData: { pageType: 'marketing' },
});

const docsDocumentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/documents',
  component: DocsDocuments,
  staticData: { pageType: 'marketing' },
});

const docsChangeManagementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/change-management',
  component: DocsChangeManagement,
  staticData: { pageType: 'marketing' },
});

const docsApprovalRulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/approval-rules',
  component: DocsApprovalRules,
  staticData: { pageType: 'marketing' },
});

const docsDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/details',
  component: DocsDetails,
  staticData: { pageType: 'marketing' },
});

const docsTeamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/teams',
  component: DocsTeams,
  staticData: { pageType: 'marketing' },
});

const docsAiIntegrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/ai-integration',
  component: DocsAiIntegration,
  staticData: { pageType: 'marketing' },
});

const blogIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/blog',
  component: BlogIndex,
  staticData: { pageType: 'marketing' },
});

// One static route per post, from the registry, so a post is a registered
// path like every other marketing page: `Link` keeps it in-SPA, and the
// sitemap can name it.
const blogPostRoutes = blogPosts.map(({ meta, component }) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: blogPostPath(meta.slug),
    component,
    staticData: { pageType: 'marketing' },
  }),
);

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: Privacy,
  staticData: { pageType: 'marketing' },
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: Terms,
  staticData: { pageType: 'marketing' },
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  pricingRoute,
  demoRoute,
  featureDocumentsRoute,
  featureApprovalsRoute,
  featureAIRoute,
  docsGettingStartedRoute,
  docsWorkspaceRoute,
  docsDocumentsRoute,
  docsChangeManagementRoute,
  docsApprovalRulesRoute,
  docsDetailsRoute,
  docsTeamsRoute,
  docsAiIntegrationRoute,
  blogIndexRoute,
  ...blogPostRoutes,
  privacyRoute,
  termsRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 10_000,
});

export type AppRouter = typeof router;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
  // Page surface read by useDocumentTitle. The marketing site only ever
  // serves `marketing` pages; the option mirrors the product app's so the
  // shared page modules typecheck unchanged.
  interface StaticDataRouteOption {
    pageType?: 'marketing';
  }
}
